import { Dispatch } from 'redux';
import {
  getAllCommissionGroups,
  type CommissionGroupListItem,
} from 'app/shared/api/commissionGroup/getCommissionGroups';
import { getCommissionGroupById } from 'app/shared/api/commissionGroup/getCommissionGroupById';
import {
  createCommissionGroups,
  updateCommissionGroups,
} from 'app/shared/api/commissionGroup/saveCommissionGroups';
import { commissionRulesToOfferRequest } from 'app/shared/api/commissionGroup/serializeCommissionOffer';
import type { CommissionRule } from './types';

// ─── Types ─────────────────────────────────────────────────────────────────────
export type CommissionCategory = CommissionGroupListItem;

// ─── Action Types ──────────────────────────────────────────────────────────────
export const FETCH_COMMISSIONS = 'COMMISSION_MANAGER/FETCH_COMMISSIONS';
export const SET_COMMISSIONS = 'COMMISSION_MANAGER/SET_COMMISSIONS';

export const SAVE_COMMISSION_REQUEST = 'COMMISSION_MANAGER/SAVE_COMMISSION_REQUEST';
export const SAVE_COMMISSION_SUCCESS = 'COMMISSION_MANAGER/SAVE_COMMISSION_SUCCESS';
export const SAVE_COMMISSION_FAILURE = 'COMMISSION_MANAGER/SAVE_COMMISSION_FAILURE';

export const COMMISSION_SET_FILTER = 'COMMISSION_MANAGER/SET_FILTER';

// ─── Action Creators ───────────────────────────────────────────────────────────
export const setCommissions = (items: CommissionCategory[]) => ({
  type: SET_COMMISSIONS,
  payload: items,
});

export const setFilter = (value: string) => ({
  type: COMMISSION_SET_FILTER,
  payload: value,
});

// ─── Thunks ────────────────────────────────────────────────────────────────────
import { getDivisionId } from 'app/shared/division/getDivisionId';

export const fetchCommissionCategories = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_COMMISSIONS });
  try {
    const divisionId = getDivisionId();
    const data = await getAllCommissionGroups(divisionId);
    dispatch(setCommissions(Array.isArray(data) ? data : []));
  } catch {
    dispatch(setCommissions([]));
  }
};

export const fetchCommissionGroupById = (id: number) => async (): Promise<CommissionCategory | null> => {
  try {
    const divisionId = getDivisionId();
    const dto = await getCommissionGroupById(divisionId, id);
    return dto as CommissionCategory | null;
  } catch {
    return null;
  }
};

export const saveCommissionGroup = (rules: CommissionRule[], editId: number | null) => async (
  dispatch: Dispatch
): Promise<{ ok: boolean; message?: string }> => {
  dispatch({ type: SAVE_COMMISSION_REQUEST });
  try {
    const divisionId = getDivisionId();
    const isExisting = (r: CommissionRule) => {
      const n = Number(r.id);
      return !r.id.startsWith('rule-') && Number.isFinite(n) && n > 0;
    };
    const toUpdateRules = rules.filter(isExisting);
    const toCreateRules = rules.filter((r) => !isExisting(r));

    if (toUpdateRules.length > 0) {
      const updateRes = await updateCommissionGroups(divisionId, commissionRulesToOfferRequest(toUpdateRules));
      if (!updateRes.ok) {
        dispatch({ type: SAVE_COMMISSION_FAILURE, error: updateRes.message });
        return { ok: false, message: updateRes.message };
      }
    }
    if (toCreateRules.length > 0) {
      const createRes = await createCommissionGroups(divisionId, commissionRulesToOfferRequest(toCreateRules));
      if (!createRes.ok) {
        dispatch({ type: SAVE_COMMISSION_FAILURE, error: createRes.message });
        return { ok: false, message: createRes.message };
      }
    }

    dispatch({ type: SAVE_COMMISSION_SUCCESS });
    await (fetchCommissionCategories() as any)(dispatch);
    return { ok: true };
  } catch (error: any) {
    const message = error?.message || 'Failed to save commission group.';
    dispatch({ type: SAVE_COMMISSION_FAILURE, error: message });
    return { ok: false, message };
  }
};